import { type TypedDocumentNode, gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { ANY_SPORT_MY_CARDS_GALLERY_RARITY_SET_LEAGUE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { COMPETITION_COLORS } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';

import { CollectionSlotsPreview } from './CollectionSlotsPreview';
import type {
  LeagueCollectionPreviewQuery,
  LeagueCollectionPreviewQueryVariables,
} from './__generated__/index.graphql';

const Root = styled.div`
  position: relative;
  isolation: isolate;
  border: 1px solid var(--c-nd-50);
  border-radius: var(--double-unit);
  overflow: hidden;
`;

const Header = styled(Vertical)`
  padding: var(--unit) 0 var(--double-unit);
  align-items: center;
  z-index: 1;
`;

const InnerWrapper = styled(Vertical)`
  padding: var(--double-unit) var(--unit) 0;
`;
const CompetitionLogo = styled.img`
  height: 56px;
  object-fit: contain;
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  z-index: -1;
`;

const SecondRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--unit);
  align-items: stretch;
`;

const StyledButton = styled(SBHorizontal)`
  padding: var(--double-unit);
`;

const SlotPreviewWrapper = styled.div`
  padding: var(--double-unit) var(--unit);
  border: 1px solid var(--c-nd-50);
  border-radius: var(--unit);
`;

const LeagueCollectionSlotPreview = (
  props: ComponentProps<typeof CollectionSlotsPreview>
) => {
  return (
    <SlotPreviewWrapper>
      <CollectionSlotsPreview {...props} />
    </SlotPreviewWrapper>
  );
};

const LEAGUE_COLLECTION_PREVIEW_QUERY = gql`
  query LeagueCollectionPreviewQuery(
    $setSlug: String!
    $seasonStartYear: Int!
    $rarity: Rarity!
    $leagueSlug: String!
    $userSlug: String!
  ) {
    currentUser {
      slug
      cardCollections(
        cardSetSlug: $setSlug
        seasonStartYears: [$seasonStartYear]
        rarities: [$rarity]
        forCompetitions: [$leagueSlug]
        first: 3
      ) {
        nodes {
          slug
          anyTeam {
            slug
            shortName
            ... on Club {
              slug
              domesticLeague {
                slug
                displayName
                pictureUrl
              }
            }
          }
          ...CollectionSlotsPreview_cardCollection
        }
      }
    }
  }
  ${CollectionSlotsPreview.fragments.cardCollection}
` as TypedDocumentNode<
  LeagueCollectionPreviewQuery,
  LeagueCollectionPreviewQueryVariables
>;
type Props = {
  setSlug: string;
  leagueSlug: string;
  rarity: Rarity;
  userSlug: string;
};

export const LeagueCollectionPreview = ({
  setSlug,
  rarity,
  leagueSlug,
  userSlug,
}: Props) => {
  const { data } = useQuery(LEAGUE_COLLECTION_PREVIEW_QUERY, {
    variables: {
      setSlug: setSlug || '',
      seasonStartYear: 2025,
      leagueSlug,
      rarity,
      userSlug,
    },
  });

  const { generateSportContextPath } = useSportContext();

  const cardCollections = data?.currentUser?.cardCollections?.nodes || [];
  const anyTeam = cardCollections.at(0)?.anyTeam;
  const competition = isType(anyTeam, 'Club') ? anyTeam.domesticLeague : null;
  return (
    <Root>
      <StyledTextureBackground
        background="wave2"
        mode="dark"
        color={COMPETITION_COLORS[leagueSlug] || 'var(--c-nd-150)'}
      />
      <NeonEdge
        color={COMPETITION_COLORS[leagueSlug] || 'var(--c-nd-150)'}
        side="top"
        spread="60%"
        showHalo="always"
      >
        <InnerWrapper>
          <Header
            as={Link}
            to={generateSportContextPath(
              ANY_SPORT_MY_CARDS_GALLERY_RARITY_SET_LEAGUE,
              {
                params: {
                  league: leagueSlug,
                  set: setSlug,
                  rarity,
                },
              }
            )}
          >
            {!!competition?.pictureUrl && (
              <CompetitionLogo
                src={competition?.pictureUrl}
                alt={competition?.displayName}
              />
            )}
          </Header>
          <LeagueCollectionSlotPreview cardCollection={cardCollections.at(0)} />
          <SecondRow>
            <LeagueCollectionSlotPreview
              cardCollection={cardCollections.at(1)}
              small
            />
            <LeagueCollectionSlotPreview
              cardCollection={cardCollections.at(2)}
              small
            />
          </SecondRow>
        </InnerWrapper>
        <StyledButton as={LinkBox}>
          <BodyM bold>
            <FormattedMessage
              id="LeagueCollectionPreview.viewAll"
              defaultMessage="View all clubs"
            />
          </BodyM>
          <LinkOther
            as={Link}
            to={generateSportContextPath(
              ANY_SPORT_MY_CARDS_GALLERY_RARITY_SET_LEAGUE,
              {
                params: {
                  league: leagueSlug,
                  set: setSlug,
                  rarity,
                },
              }
            )}
          >
            <IconButton color="secondary" size="small">
              <ChevronRightBold />
            </IconButton>
          </LinkOther>
        </StyledButton>
      </NeonEdge>
    </Root>
  );
};
